/* ============================================
   Conversaciones silenciadas — store compartido

   Qué chats silenció ESTE usuario. Vive fuera de React porque lo leen dos
   árboles que no se tocan: la página de Conversaciones (para el menú y la
   campana tachada) y `useChatNotifications`, que se monta arriba en el
   AppLayout y decide si suena o no. Pasarlo por props o por contexto
   obligaría a levantar el estado hasta la raíz para un dato de dos líneas.
   ============================================ */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

/** Set inmutable: se reemplaza entero en cada cambio para que `useSyncExternalStore` lo compare por identidad. */
let silenciadas: ReadonlySet<number> = new Set()
let cargando: Promise<void> | null = null

const oyentes = new Set<() => void>()

function publicar(nuevo: ReadonlySet<number>) {
  silenciadas = nuevo
  oyentes.forEach((fn) => fn())
}

function token(): string | null {
  return localStorage.getItem('auth_token')
}

/** Carga la lista una sola vez por sesión. Llamadas concurrentes comparten la misma promesa. */
export function cargarSilenciadas(): Promise<void> {
  if (cargando) return cargando

  cargando = (async () => {
    const jwt = token()
    if (!jwt) return
    try {
      const res = await fetch(`${API_URL}/api/chat/silenciadas`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
      if (!res.ok) return
      const ids = (await res.json()) as number[]
      publicar(new Set(ids))
    } catch {
      // Sin lista: nada silenciado. Que falle la preferencia no puede romper el chat.
    }
  })()

  return cargando
}

export function estaSilenciada(id: number): boolean {
  return silenciadas.has(id)
}

/**
 * Silencia o reactiva un chat. Aplica el cambio de inmediato (la UI no espera
 * a la red) y lo revierte si el backend lo rechaza.
 * Devuelve el estado final.
 */
export async function alternarSilencio(id: number): Promise<boolean> {
  const previo = silenciadas
  const objetivo = !silenciadas.has(id)

  const optimista = new Set(silenciadas)
  if (objetivo) optimista.add(id)
  else optimista.delete(id)
  publicar(optimista)

  const jwt = token()
  if (!jwt) { publicar(previo); return !objetivo }

  try {
    const res = await fetch(`${API_URL}/api/chat/conversaciones/${id}/silenciar`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ silenciada: objetivo }),
    })
    if (!res.ok) throw new Error(String(res.status))
    return objetivo
  } catch {
    publicar(previo)
    throw new Error('No se pudo guardar la preferencia')
  }
}

/** Suscripción para `useSyncExternalStore`. Devuelve la baja. */
export function suscribirSilenciadas(fn: () => void): () => void {
  oyentes.add(fn)
  return () => { oyentes.delete(fn) }
}

/** Snapshot estable: la misma referencia mientras no haya cambios. */
export function snapshotSilenciadas(): ReadonlySet<number> {
  return silenciadas
}
